import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl } from "react-intl";
import { Box, Button, Grid, InputAdornment, Stack, Typography } from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { LoadingSpinner, Input, Select, ReadOnlyInput } from "../";
import { CF, COGNOME, EMAIL, NOME, TELEFONO, getUserInfoFromStore } from "../../modules/common";
import {
  getIstitutiScolastici,
  fetchIstitutiScolasticiEnded,
  getIncarichi,
  fetchIncarichiEnded,
  inserimentoProfiloEnded,
  getProfilo,
  aggiornaProfilo,
  inserisciProfilo,
  PROFILO_NOME,
  PROFILO_COGNOME,
  PROFILO_EMAIL,
  PROFILO_CELLULARE,
  PROFILO_CODICE_SCUOLA,
  PROFILO_NOME_SCUOLA,
  PROFILO_CODICE_INCARICO,
  PROFILO_INCARICO,
  PROFILO_RUOLO,
  PROFILO_ISCRIZIONE_NEWSLETTER,
} from "../../modules/osservatorio";
import {
  REQUIRED_FIELD,
  NOME_PLACEHOLDER,
  COGNOME_PLACEHOLDER,
  EMAIL_ERROR,
  CF_PLACEHOLDER,
  EMAIL_LABEL,
  EMAIL_PLACEHOLDER,
  CELLULARE_PLACEHOLDER,
  CELLULARE_LABEL,
  CELLULARE_ERROR,
  CELLULARE_ERROR_LENGTH,
  ISTITUTO_LABEL,
  ISTITUTO_PLACEHOLDER,
  INCARICO_LABEL,
  INCARICO_PLACEHOLDER,
  CAMPI_OBBLIGATORI_LABEL,
  SALVA,
} from "../../modules/language";
import LoghiCoesioneIcon from "../../styles/images/LoghiCoesione.png";
import { LogoScuolaDigitaleLiguria as LogoScuolaDigitaleLiguriaIcon } from "../../styles/images/LogoScuolaDigitaleLiguria";
import { getMessageString } from "../../utility";
import { InserimentoError } from "./InserimentoError";
import { CheckboxConsensi } from "./CheckboxConsensi";

const validateEmail = (email) => {
  // prettier-ignore
  const regex = /^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))[;]){0,}((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))$/;
  return regex.test(email.toLowerCase());
};

const validateCellulare = (cellulare) => {
  // prettier-ignore
  const regex = /^((3[1-9][0-9]))(\d{7})$/;
  return regex.test(cellulare);
};

const FORM_EMAIL = "email";
const FORM_CELLULARE = "cellulare";
const FORM_ISTITUTO = "istituto";
const FORM_INCARICO = "incarico";
const FORM_AUTOCERTIFICAZIONE = "autocertificazione";
const FORM_PRIVACY = "privacy";
const FORM_NEWSLETTER = "newsletter";

export const FormRegistrazione = injectIntl(({ intl }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(getUserInfoFromStore);
  const istitutiScolastici = useSelector(getIstitutiScolastici);
  const istitutiScolasticiEnded = useSelector(fetchIstitutiScolasticiEnded);
  const profilo = useSelector(getProfilo);
  const incarichi = useSelector(getIncarichi);
  const incarichiEnded = useSelector(fetchIncarichiEnded);
  const inserimentoCompletato = useSelector(inserimentoProfiloEnded);
  const [saveButtonPressed, setSaveButtonPressed] = useState(false);
  const [showError, setShowError] = useState(false);

  const validationSchema = yup.object({
    [FORM_EMAIL]: yup
      .string()
      .required(getMessageString(intl, REQUIRED_FIELD))
      .test({
        message: getMessageString(intl, EMAIL_ERROR),
        test: (value) => value !== "" && validateEmail(value),
      }),
    [FORM_CELLULARE]: yup
      .string()
      .required(getMessageString(intl, REQUIRED_FIELD))
      .test({
        test: (value, ctx) => {
          if (value !== "" && value.length < 10) return ctx.createError({ message: getMessageString(intl, CELLULARE_ERROR_LENGTH) });
          else if (value !== "" && value.length === 10 && !validateCellulare(value))
            return ctx.createError({ message: getMessageString(intl, CELLULARE_ERROR) });
          return true;
        },
      }),

    [FORM_ISTITUTO]: yup.object().required(getMessageString(intl, REQUIRED_FIELD)),
    [FORM_INCARICO]: yup.object().required(getMessageString(intl, REQUIRED_FIELD)),
    [FORM_AUTOCERTIFICAZIONE]: yup
      .boolean()
      .required(getMessageString(intl, REQUIRED_FIELD))
      .test({
        message: getMessageString(intl, REQUIRED_FIELD),
        test: (value) => value,
      }),
    [FORM_PRIVACY]: yup
      .boolean()
      .required(getMessageString(intl, REQUIRED_FIELD))
      .test({
        message: getMessageString(intl, REQUIRED_FIELD),
        test: (value) => value,
      }),
  });

  const formik = useFormik({
    initialValues: {
      [FORM_EMAIL]: "",
      [FORM_CELLULARE]: "",
      [FORM_ISTITUTO]: null,
      [FORM_INCARICO]: null,
      [FORM_AUTOCERTIFICAZIONE]: false,
      [FORM_PRIVACY]: false,
      [FORM_NEWSLETTER]: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      setSaveButtonPressed(true);
      if (profilo) {
        const cambiRuoloScuola = values[FORM_ISTITUTO].codice !== profilo[PROFILO_CODICE_SCUOLA] || values[FORM_INCARICO].id !== profilo[PROFILO_CODICE_INCARICO]; // prettier-ignore
        let profiloUpdated = Object.assign({}, profilo);
        profiloUpdated[PROFILO_NOME] = userInfo[NOME];
        profiloUpdated[PROFILO_COGNOME] = userInfo[COGNOME];
        profiloUpdated[PROFILO_EMAIL] = values[FORM_EMAIL];
        profiloUpdated[PROFILO_CELLULARE] = values[FORM_CELLULARE];
        profiloUpdated[PROFILO_CODICE_SCUOLA] = values[FORM_ISTITUTO].codice;
        profiloUpdated[PROFILO_NOME_SCUOLA] = values[FORM_ISTITUTO].descrizione;
        profiloUpdated[PROFILO_CODICE_INCARICO] = values[FORM_INCARICO].id;
        profiloUpdated[PROFILO_INCARICO] = values[FORM_INCARICO].descrizione;
        profiloUpdated[PROFILO_ISCRIZIONE_NEWSLETTER] = values[FORM_NEWSLETTER];
        dispatch(aggiornaProfilo({ payload: profiloUpdated, cambiRuoloScuola }));
      } else
        dispatch(
          inserisciProfilo({
            nome: userInfo[NOME],
            cognome: userInfo[COGNOME],
            codiceFiscale: userInfo[CF],
            email: values[FORM_EMAIL],
            cellulare: values[FORM_CELLULARE],
            istituto: values[FORM_ISTITUTO],
            incarico: values[FORM_INCARICO],
            newsletterAccepted: values[FORM_NEWSLETTER],
          })
        );
    },
  });

  useEffect(() => {
    if (userInfo) {
      if (userInfo[EMAIL] != null) formik.setFieldValue(FORM_EMAIL, userInfo[EMAIL]);
      if (userInfo[TELEFONO] != null) formik.setFieldValue(FORM_CELLULARE, userInfo[TELEFONO]);
    }
    if (profilo) {
      if (profilo[PROFILO_EMAIL] != null) formik.setFieldValue(FORM_EMAIL, profilo[PROFILO_EMAIL]);
      if (profilo[PROFILO_CELLULARE] != null) formik.setFieldValue(FORM_CELLULARE, profilo[PROFILO_CELLULARE]);
      if (istitutiScolastici?.length > 0 && profilo[PROFILO_CODICE_SCUOLA] != null) formik.setFieldValue(FORM_ISTITUTO, istitutiScolastici.find((istituto) => istituto.codice === profilo[PROFILO_CODICE_SCUOLA])); // prettier-ignore
      if (incarichi?.length > 0 && profilo[PROFILO_CODICE_INCARICO] != null) formik.setFieldValue(FORM_INCARICO, incarichi.find((incarico) => incarico.id === profilo[PROFILO_CODICE_INCARICO])); // prettier-ignore
    }
    // eslint-disable-next-line
  }, [profilo, userInfo, istitutiScolastici, incarichi]);

  useEffect(() => {
    if (inserimentoCompletato) {
      setSaveButtonPressed(false);
      setShowError(true);
    }
    // eslint-disable-next-line
  }, [inserimentoCompletato]);

  return (
    <Box sx={{ display: "grid", alignItems: "center", height: "100%" }}>
      <form onSubmit={formik.handleSubmit}>
        <Box className="formRegistrazione">
          <Stack className="header">
            <LogoScuolaDigitaleLiguriaIcon />
            <Typography variant="h1">Scuola Digitale Liguria</Typography>
            <img src={LoghiCoesioneIcon} alt="Loghi FSE Coesione" />
          </Stack>
          <Stack className="content">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ReadOnlyInput placeholder={getMessageString(intl, NOME_PLACEHOLDER)} value={userInfo[NOME]} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ReadOnlyInput placeholder={getMessageString(intl, COGNOME_PLACEHOLDER)} value={userInfo[COGNOME]} />
              </Grid>
              <Grid item xs={12} md={12}>
                <ReadOnlyInput placeholder={getMessageString(intl, CF_PLACEHOLDER)} value={userInfo[CF]} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  required
                  label={getMessageString(intl, EMAIL_LABEL)}
                  placeholder={getMessageString(intl, EMAIL_PLACEHOLDER)}
                  value={formik.values[FORM_EMAIL]}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^[a-z0-9@.]*$/i;
                    if (regex.test(value)) formik.setFieldValue(FORM_EMAIL, value);
                  }}
                  {...((formik.values[FORM_EMAIL]?.length > 0 || formik.touched[FORM_EMAIL]) &&
                    formik.errors[FORM_EMAIL] != null && { hasError: true, errorText: formik.errors[FORM_EMAIL] })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  required
                  label={getMessageString(intl, CELLULARE_LABEL)}
                  placeholder={getMessageString(intl, CELLULARE_PLACEHOLDER)}
                  value={formik.values[FORM_CELLULARE]}
                  onChange={(e) => {
                    const value = e.target.value;
                    const regex = /^[0-9]*$/i;
                    if (regex.test(value) && value.length < 11) formik.setFieldValue(FORM_CELLULARE, value);
                  }}
                  startAdornment={
                    <InputAdornment
                      position="start"
                      {...((formik.values[FORM_CELLULARE]?.length > 0 || formik.touched[FORM_CELLULARE]) &&
                        formik.errors[FORM_CELLULARE] != null && {
                          sx: { "& .MuiTypography-root": { color: "#871d1d !important" } },
                        })}
                    >
                      + 39
                    </InputAdornment>
                  }
                  {...((formik.values[FORM_CELLULARE]?.length > 0 || formik.touched[FORM_CELLULARE]) &&
                    formik.errors[FORM_CELLULARE] != null && {
                      hasError: true,
                      errorText: formik.errors[FORM_CELLULARE],
                    })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  required
                  label={getMessageString(intl, ISTITUTO_LABEL)}
                  placeholder={getMessageString(intl, ISTITUTO_PLACEHOLDER)}
                  options={
                    formik.values[FORM_INCARICO] == null || (formik.values[FORM_INCARICO] != null && formik.values[FORM_INCARICO].id !== 9)
                      ? istitutiScolastici
                      : istitutiScolastici.filter((istituto) => istituto.codice === "TEST01000V")
                  }
                  value={formik.values[FORM_ISTITUTO]}
                  onChange={(e, v) => formik.setFieldValue(FORM_ISTITUTO, v)}
                  {...(formik.touched[FORM_ISTITUTO] &&
                    formik.errors[FORM_ISTITUTO] != null && { hasError: true, errorText: formik.errors[FORM_ISTITUTO] })}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((item) => {
                      const { descrizione, codice } = item;
                      if (codice != null)
                        return (
                          descrizione.toUpperCase().includes(inputValue.toUpperCase()) || codice.toUpperCase().includes(inputValue.toUpperCase())
                        );
                      else return descrizione.toUpperCase().includes(inputValue.toUpperCase());
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Select
                  required
                  label={getMessageString(intl, INCARICO_LABEL)}
                  placeholder={getMessageString(intl, INCARICO_PLACEHOLDER)}
                  options={incarichi.filter((incarico) =>
                    profilo
                      ? incarico.visibility.includes(profilo[PROFILO_RUOLO])
                      : incarico.visibility.filter((inc) => ["SDG_GES", "SDG_IST"].includes(inc))
                  )}
                  value={formik.values[FORM_INCARICO]}
                  onChange={(e, v) => {
                    formik.setFieldValue(FORM_INCARICO, v);
                    if (v?.id === 9) formik.setFieldValue(FORM_ISTITUTO, istitutiScolastici.find((istituto) => istituto.codice === "TEST01000V")); // prettier-ignore
                  }}
                  {...(formik.touched[FORM_INCARICO] &&
                    formik.errors[FORM_INCARICO] != null && { hasError: true, errorText: formik.errors[FORM_INCARICO] })}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((item) => item.descrizione.toUpperCase().includes(inputValue.toUpperCase()))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <CheckboxConsensi formik={formik} autocertificazione={FORM_AUTOCERTIFICAZIONE} privacy={FORM_PRIVACY} newsletter={FORM_NEWSLETTER} />
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography sx={{ fontWeight: 600, fontStyle: "oblique" }}>{getMessageString(intl, CAMPI_OBBLIGATORI_LABEL)}</Typography>
              </Grid>
              <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                <Button variant="contained" type="submit">
                  {getMessageString(intl, SALVA)}
                </Button>
              </Grid>
            </Grid>
          </Stack>
          <LoadingSpinner loading={(!istitutiScolasticiEnded && !incarichiEnded) || saveButtonPressed} />
          <InserimentoError open={showError} onClose={() => setShowError(false)} />
        </Box>
      </form>
    </Box>
  );
});
